// `/home` AND `/home/:appealId` — THE APP'S PROPERTIES TAB AND PROPERTY PAGE (WEB-PARITY-7, HM-1 to HM-7; ruling D5).
//
// NOT THE APP'S HOME TAB. That tab always shows EXAMPLE figures (`HomeHeroModel.illustrationMode`, HHM:36); the owner's
// own appeals, their status and their savings live on the Properties tab (PropertiesTab.swift, HomeCard.swift) and the
// property page (HomeDetailView.swift), and that is what these two screens are. The one real figure on the Home tab — the
// community card (HomeTab.swift:184-202) — is here too, under the same rule the app keeps: shown only when the server's
// aggregate is `available`, so a new project shows nothing rather than zeros (HM-6).
//
// EVERY FIGURE IS THE SERVER'S. The card's "Est. tax savings ≈ $X/yr" or "Requested", the page's "Requested reduction",
// each appeal's "Saved −$X" or "Est. −$X (N%)" are `AppealSummary`'s — `v2Outcomes` `list` works them out with its ports of
// the app's accessors (`functions/src/v2/outcomes/summary.ts`) — and the grouping into properties is the server's key
// (`propertyKey`, the port of `Home.normalizedKey`). This client adds nothing up (§3 row 25): no total, no sum.
//
// "DELETE PROPERTY" (WALK-FIX-2; PropertiesTab.swift:56-73, :135-143). Under each property, the app's quiet "Remove"
// (accessibility label "Remove property"); it opens the app's confirmation — "Delete this property?", "Cancel" and the
// destructive "Delete property", the only way to the delete. On the website the property's appeals are the ACCOUNT's, so
// the delete is the server's `deleteProperty` op: it deletes every appeal the list grouped as this property, and nothing
// else; credits are not touched (a spent credit stays spent). The app's message says "from this device", false here, so
// the website's is a draft (`./homeDrafts.ts`) shown only on a dev build with `?drafts=1`.
//
// WHAT THE WEBSITE DOES NOT HAVE, IT DOES NOT SAY (R112, the route half): no "Add property" and no "Save a property"
// (the website cannot save one), no "Exported" and no "How did it go? Log the outcome" (it records no export). "Log the
// outcome" is offered once the appeal's letter has shipped — the website's deliverable, the moment the app's own button
// would follow an export — and "Update outcome" once one is logged (HomeDetailView.swift:112-137).

import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import type { AppealSnapshot, AppealSummary, SubjectView } from "@contract";
import { signedIn, useWebAccounts } from "../app/accounts";
import { useSession } from "../app/session";
import { forgetSignature } from "../app/signature";
import { useAction, useCall } from "../app/useCall";
import { AppAlert } from "../components/AppAlert";
import { afterTemplate, afterText, type AfterFilingId } from "../components/afterFilingCopy";
import { HeldLine } from "../components/copy";
import { heldText } from "../components/heldCopy";
import { Actions } from "../components/layout";
import { Refusal } from "../components/Refusal";
import { word } from "./steps/reviewWording";
import { FailureLine } from "./steps/reviewParts";
import { deletePropertyMessage } from "./homeDrafts";
import { dollars, longDate, money } from "../format";
import {
  AppSay, AppSayTemplate, Badge, groupProperties, propertyTitle, reopenPath, tintOf, type PropertyGroup,
} from "./afterFilingParts";

/** The next-action line of the app's card (HomeCard.nextAction, :106-124), for the newest appeal. */
function nextAction(appeal: AppealSnapshot): AfterFilingId {
  const outcome = appeal.outcome?.result;
  switch (outcome) {
    case "won":
    case "partiallyWon":
      return "home.next.won";
    case "filed":
      return "home.next.filed";
    case "denied":
      return "home.next.denied";
    case "raised":
      return "home.next.raised";
    case "withdrawn":
      return "home.next.withdrawn";
    case undefined:
      return appeal.letter.status === "shipped" ? "home.next.generated" : "home.next.draft";
  }
}

/** The card's money metric (HomeCard.swift:42-58): the yearly saving when the ask lowers the bill and the server can state
 *  one; else the ask, labelled "would not lower your bill" when it would not; else nothing. */
function MoneyMetric({ appeal, summary }: { appeal: AppealSnapshot; summary: AppealSummary | null }) {
  const annual = summary?.projectedAnnualSaving.annual ?? null;
  if (summary?.askPosture === "reducesBill" && annual !== null) {
    return (
      <span className="metric savings" data-metric="estSavings">
        <AppSay id="home.card.estSavings" as="em" />
        <AppSayTemplate id="home.card.estSavingsValue" values={{ amount: dollars(annual) }} as="b" />
      </span>
    );
  }
  if (appeal.requestedValue === null) return null;
  const raises = summary?.askPosture === "wouldNotLowerBill";
  return (
    <span className={`metric ${raises ? "muted" : "savings"}`} data-metric="requested">
      {raises ? (
        <AppSayTemplate id="home.card.requestedNoLower" values={{ note: afterText("home.card.wouldNotLower") }} as="em" />
      ) : (
        <AppSay id="home.card.requested" as="em" />
      )}
      <b>{money(appeal.requestedValue)}</b>
    </span>
  );
}

function PropertyCard({ group }: { group: PropertyGroup }) {
  const current = group.appeals[0];
  const assessed = current.snapshot.subject?.assessedValue?.value ?? null;
  const tone = current.snapshot.outcome !== null ? `tint-${tintOf(current.snapshot.outcome.result)}` : "accent";
  return (
    <Link className={`property-card ${tone}`} to={`/home/${current.snapshot.appealId}`} data-property-key={group.key}>
      <span className="property-head">
        <span className="addr">{propertyTitle(group)}</span>
        <Badge appeal={current.snapshot} />
      </span>
      <span className="metrics">
        {assessed !== null && (
          <span className="metric" data-metric="assessed">
            <AppSay id="home.card.assessed" as="em" />
            <b>{money(assessed)}</b>
          </span>
        )}
        <MoneyMetric appeal={current.snapshot} summary={current.summary} />
      </span>
      <span className="next-action">
        <span className="arrow" aria-hidden="true">→</span>
        <AppSay id={nextAction(current.snapshot)} as="span" />
        {group.appeals.length > 1 && (
          <AppSayTemplate id="home.card.appeals" values={{ n: group.appeals.length }} as="em" />
        )}
      </span>
    </Link>
  );
}

/** The app's trash symbol on "Remove" (PropertiesTab.swift:138, `systemImage: "trash"`). */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M9 3h6l1 2h4v2H4V5h4zm-3 6h12l-1 12H7zm4 2v8h1.6v-8zm2.4 0v8H14v-8z"
      />
    </svg>
  );
}

/** HM-6: the community card, only when the server's aggregate cleared its gate (HomeTab.swift:184-186). */
function CommunityCard() {
  const session = useSession();
  const stats = useCall(() => session.transport.v2Outcomes.stats({}), [session.transport]);
  if (stats.status !== "ok") return null;
  const s = stats.response.body.stats;
  if (s === undefined || !s.available) return null;
  const total = s.totalAnnualSavings;
  return (
    <section className="card community" data-community-card data-sample-size={s.sampleSize}>
      <AppSay id="home.community.title" as="h3" />
      <div className="community-stats">
        <span>
          <AppSayTemplate id="home.community.count" values={{ n: s.sampleSize }} as="b" />
          <AppSay id="home.community.countLabel" as="em" />
        </span>
        {total !== null && total > 0 && (
          <span>
            <b>{dollars(total)}</b>
            <AppSay id="home.community.savedLabel" as="em" />
          </span>
        )}
      </div>
      <AppSay id="home.community.footnote" className="faint small" />
    </section>
  );
}

/** "Start an appeal": a new address, from `/`. A PAID appeal the tab holds is let go first — another address on it is
 *  another appeal, and the server refuses to re-resolve it (WEB-FLOW-ORDER); an unpaid one is kept, as before. */
function useStartAnother(appeals: readonly AppealSnapshot[]): (address?: string) => void {
  const session = useSession();
  const navigate = useNavigate();
  return (address) => {
    const held = appeals.find((a) => a.appealId === session.appealId);
    if (held !== undefined && held.entitlement.unlocked) session.forgetAppeal();
    if (address !== undefined) session.setTypedAddress(address);
    navigate("/");
  };
}

/** F2 for a browser signed out of any account, with the server's WEB_ACCOUNTS_ENABLED on (ACC-3a). */
function useSignedOut(): boolean {
  const session = useSession();
  return useWebAccounts() === true && !signedIn(session.auth);
}

function SignedOutAppeals() {
  return (
    <div className="card signed-out" data-account-screen="home-signed-out">
      <p className="copy" data-held-copy="account.signedOut.appeals">{heldText("account.signedOut.appeals")}</p>
      <Link className="btn primary" to="/signin" state={{ from: "/home" }} data-held-copy="account.signIn.button">
        {heldText("account.signIn.button")}
      </Link>
    </div>
  );
}

export function Home() {
  const session = useSession();
  const signedOut = useSignedOut();
  const accountList = useWebAccounts() === true && signedIn(session.auth);
  const list = useCall(() => session.transport.v2Outcomes.list(), [session.transport, session.revision]);
  const appeals = list.status === "ok" ? list.response.body.appeals ?? [] : [];
  const start = useStartAnother(appeals);
  const [pendingDelete, setPendingDelete] = useState<PropertyGroup | null>(null);
  const remove = useAction((appealId: string) => session.transport.v2GenerateLetter.deleteProperty({ appealId }));

  if (list.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your appeals…
      </p>
    );
  }
  if (list.status === "failed") return <Refusal failure={list.failure} context="Your appeals" />;
  const groups = groupProperties(appeals, list.response.body.summaries);

  const deleteProperty = async (group: PropertyGroup) => {
    setPendingDelete(null);
    // The server works out the property's appeals from the one named; the newest is the card's own.
    const result = await remove.run(group.appeals[0].snapshot.appealId);
    if (!result.ok) return;
    const gone = result.response.body.appealIds;
    for (const id of gone) forgetSignature(id);
    // An appeal this tab was holding is gone with its property: let go of it, as "Delete this appeal" does.
    if (session.appealId !== null && gone.includes(session.appealId)) session.forgetAppeal();
    session.refresh();
  };
  const deleteMessage = pendingDelete !== null ? deletePropertyMessage(propertyTitle(pendingDelete), pendingDelete.appeals.length) : null;

  return (
    <div className="properties" data-properties-screen>
      <AppSay id="home.navTitle" as="h1" />

      {signedOut ? (
        <SignedOutAppeals />
      ) : groups.length === 0 ? (
        <div className="card empty" data-empty>
          <AppSay id="home.empty" className="muted" />
        </div>
      ) : (
        <div className="property-cards">
          {groups.map((group) => (
            <div className="property-block" key={group.key}>
              <PropertyCard group={group} />
              <div className="property-tools">
                <button
                  type="button"
                  className="btn quiet small remove-property"
                  aria-label={afterText("home.remove.label")}
                  data-app-copy="home.remove"
                  data-remove-property={group.key}
                  disabled={remove.state.status === "pending"}
                  onClick={() => setPendingDelete(group)}
                >
                  <TrashIcon />
                  {afterText("home.remove")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {remove.state.status === "failed" && (
        <div className="card" data-delete-property-failed>
          <FailureLine failure={remove.state.failure} />
        </div>
      )}
      {pendingDelete !== null && (
        <AppAlert
          title={afterText("home.delete.title")}
          titleId="home.delete.title"
          kind="deleteProperty"
          buttons={[
            { label: afterText("home.delete.cancel"), wordsId: "home.delete.cancel", cancel: true, onPress: () => setPendingDelete(null) },
            // The app's `role: .destructive` (PropertiesTab.swift:64): the danger colour, never the amber primary.
            { label: afterText("home.delete.confirm"), wordsId: "home.delete.confirm", destructive: true, onPress: () => void deleteProperty(pendingDelete) },
          ]}
        >
          {deleteMessage !== null && <p data-approved="home.delete.message">{deleteMessage}</p>}
        </AppAlert>
      )}

      {!signedOut && <CommunityCard />}

      {/* L7 (ACC-3b): an account's list says where its app appeals are — they stay on the phone until appeals follow the
          account (ACCOUNTS_PLAN §2.9, decision 8). Only with the switch on and an account signed in. */}
      {accountList && <HeldLine id="account.l7.appAppeals" className="faint small" />}

      <Actions>
        <button className="btn primary" type="button" onClick={() => start()} data-app-copy="home.start">
          {afterText("home.start")}
        </button>
      </Actions>
    </div>
  );
}

/** HomeDetailView.characteristics (:208-215): type · beds · baths · sqft, each only when known. */
function characteristics(subject: SubjectView | null): string {
  if (subject === null) return "";
  const parts: string[] = [];
  if (subject.propertyType !== null) parts.push(subject.propertyType.value);
  if (subject.beds !== null) parts.push(afterTemplate("home.page.beds", { n: subject.beds.value }));
  if (subject.baths !== null) {
    const b = subject.baths.value;
    parts.push(afterTemplate("home.page.baths", { n: Number.isInteger(b) ? String(b) : b.toFixed(1) }));
  }
  if (subject.sqft !== null) parts.push(afterTemplate("home.page.sqft", { n: Math.trunc(subject.sqft.value) }));
  return parts.join(" · ");
}

/** One appeal row of the property page (HomeDetailView.appealRow, :141-177, and its buttons, :79-116). */
function AppealRow({ appeal, summary }: { appeal: AppealSnapshot; summary: AppealSummary | null }) {
  const session = useSession();
  const navigate = useNavigate();
  const open = (path: string) => {
    session.openAppeal(appeal.appealId);
    navigate(path);
  };
  const raised = appeal.outcome?.result === "raised";
  const realized = summary?.realizedReduction ?? null;
  const estimate = summary?.estimatedReduction ?? null;
  const year = summary?.year ?? Number(appeal.createdAt.slice(0, 4));
  const body = (
    <>
      <span className="row-head">
        <AppSayTemplate id="home.row.year" values={{ year }} as="b" />
        <Badge appeal={appeal} />
      </span>
      <span className="row-meta">
        {raised ? (
          <AppSay id="home.row.increased" as="em" className="danger" />
        ) : appeal.outcome !== null && realized !== null ? (
          <AppSayTemplate id="home.row.saved" values={{ amount: money(realized) }} as="em" className="savings" />
        ) : estimate !== null ? (
          <AppSayTemplate
            id="home.row.estimated"
            values={{ amount: money(estimate.amount), percent: estimate.percent }}
            as="em"
            className="savings"
          />
        ) : null}
        <span className="faint" data-updated-at={appeal.updatedAt}>{longDate(appeal.updatedAt.slice(0, 10))}</span>
      </span>
    </>
  );
  // THE ROW'S WAY IN IS A BUTTON THAT SAYS WHERE IT GOES (WALK-FIX-1). The app's whole row is the button that reopens
  // the appeal (HomeDetailView.swift:79-112), drawn with no chevron; on the website the same row read as plain text, and
  // Tyler, 2026-10-05, reached his letter only by clicking the "GENERATED" badge inside it. So the row is the facts again
  // (the year, the badge, which stays a badge, the figure) and its way in is a real button, in the app's words: "See my
  // Appeal Letter" (InlineLetterPreview.swift:100, the app's own button that opens the letter) once one has shipped,
  // "Finish your draft" (HomeCard.swift:120) before. Each goes where the app's row goes (`reopenPath`).
  const shipped = appeal.letter.status === "shipped";
  return (
    <li className="appeal-row" data-appeal-id={appeal.appealId}>
      <div className="row-open">{body}</div>
      <div className="row-actions">
        {appeal.entitlement.unlocked && (shipped ? (
          <button type="button" className="btn ghost small" onClick={() => open(reopenPath(appeal))} data-held-copy="preview.open" data-row-open>
            {word("preview.open")}
          </button>
        ) : (
          <button type="button" className="btn ghost small" onClick={() => open(reopenPath(appeal))} data-app-copy="home.next.draft" data-row-open>
            {afterText("home.next.draft")}
          </button>
        ))}
        {appeal.outcome === null && shipped ? (
          <button type="button" className="btn savings small" onClick={() => open("/outcome")} data-app-copy="home.row.logOutcome">
            {afterText("home.row.logOutcome")}
          </button>
        ) : appeal.outcome !== null ? (
          <button type="button" className="btn ghost small" onClick={() => open("/outcome")} data-app-copy="home.row.updateOutcome">
            {afterText("home.row.updateOutcome")}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function PropertyPage() {
  const session = useSession();
  const signedOut = useSignedOut();
  const { appealId } = useParams();
  const list = useCall(() => session.transport.v2Outcomes.list(), [session.transport, session.revision]);
  const appeals = list.status === "ok" ? list.response.body.appeals ?? [] : [];
  const start = useStartAnother(appeals);

  if (signedOut) return <SignedOutAppeals />;
  if (list.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your appeals…
      </p>
    );
  }
  if (list.status === "failed") return <Refusal failure={list.failure} context="Your appeals" />;
  const group = groupProperties(appeals, list.response.body.summaries)
    .find((g) => g.appeals.some((a) => a.snapshot.appealId === appealId));
  // Not one of this account's appeals (or no longer): the list is where the owner's appeals are.
  if (group === undefined) return <Navigate to="/home" replace />;

  const current = group.appeals[0];
  const subject = current.snapshot.subject;
  const assessed = subject?.assessedValue?.value ?? null;
  const requestedReduction = current.summary?.estimatedReduction?.amount ?? null;
  const traits = characteristics(subject);
  const title = propertyTitle(group);

  return (
    <div className="property-page" data-property-page={group.key}>
      <h1>{title}</h1>

      <section className="card" data-section="property">
        <AppSay id="home.page.property" as="h3" />
        {assessed !== null && (
          <div className="kv">
            <AppSay id="home.page.assessedValue" as="span" />
            <span>{money(assessed)}</span>
          </div>
        )}
        {requestedReduction !== null && (
          <div className="kv hero">
            <AppSay id="home.page.requestedReduction" as="span" />
            <span>{money(requestedReduction)}</span>
          </div>
        )}
        {traits !== "" && <p className="muted" data-characteristics>{traits}</p>}
      </section>

      <section className="card" data-section="appeals">
        <AppSay id="home.page.appeals" as="h3" />
        <ul className="appeal-rows">
          {group.appeals.map(({ snapshot, summary }) => (
            <AppealRow key={snapshot.appealId} appeal={snapshot} summary={summary} />
          ))}
        </ul>
      </section>

      <Actions>
        <Link className="btn ghost" to="/home" data-app-copy="home.navTitle">
          {afterText("home.navTitle")}
        </Link>
        <button
          className="btn primary"
          type="button"
          onClick={() => start(subject?.address)}
          data-app-copy="home.reappeal"
        >
          {afterText("home.reappeal")}
        </button>
      </Actions>
    </div>
  );
}
